// @file The store for surface onboarding panel displayed by surface_app.vue
import { captureFetchException } from '@@/bits/error_tracker'
import window from '@@/bits/global'
import { WallCreatedFromType } from '@@/enums'
import type { OnboardingWallViz } from '@@/types'
import { defineStore } from 'pinia'
import { useSurfaceStartingStateStore } from 'pinia/surface_starting_state'
import { useWindowSizeStore } from 'pinia/window_size'
import PadletApi from 'surface/padlet_api'
import { computed, ref } from 'vue'

const PANEL_SIZES = {
  DESKTOP: 480,
  SMALLER_THAN_DESKTOP: 400,
}

const WALL_LAYOUT_THUMBNAIL_WIDTHS = {
  DESKTOP: 139,
  SMALLER_THAN_DESKTOP: 112,
}

const WALL_LAYOUT_THUMBNAIL_HEIGHTS = {
  DESKTOP: 86,
  SMALLER_THAN_DESKTOP: 86,
}

export const useSurfaceOnboardingPanelStore = defineStore('surfaceOnboardingPanel', () => {
  const isSmallerThanDesktop = computed(() => useWindowSizeStore().isSmallerThanDesktop)
  // State
  const initialOnboardingPanelState = computed<boolean>(
    () => useSurfaceStartingStateStore().startingState.onboardingFlow?.showOnboardingPanel ?? false,
  )
  const formatSelected = ref('Wall')
  const hasOnboardingCompleted = ref(false)
  const randomTitle = computed<string>(
    () => useSurfaceStartingStateStore().startingState.onboardingFlow?.blankTemplateData?.randomTitle,
  )
  const wallLayouts = computed<OnboardingWallViz[]>(
    () => useSurfaceStartingStateStore().startingState.onboardingFlow?.blankTemplateData?.layouts,
  )
  // Getters

  const showOnboardingPanel = computed(() => initialOnboardingPanelState.value && !hasOnboardingCompleted.value)

  const galleryOnboardingFlowData = computed(
    () => useSurfaceStartingStateStore().startingState.onboardingFlow?.galleryTemplateData,
  )

  const isGalleryTemplatePanel = computed(
    () =>
      useSurfaceStartingStateStore().startingState.onboardingFlow?.wallCreatedFromType === WallCreatedFromType.gallery,
  )

  const panelWidth = computed(() => {
    if (!showOnboardingPanel.value) return 0
    return isSmallerThanDesktop.value ? PANEL_SIZES.SMALLER_THAN_DESKTOP : PANEL_SIZES.DESKTOP
  })

  const layoutWidth = computed(() => {
    return isSmallerThanDesktop.value
      ? WALL_LAYOUT_THUMBNAIL_WIDTHS.SMALLER_THAN_DESKTOP
      : WALL_LAYOUT_THUMBNAIL_WIDTHS.DESKTOP
  })

  const layoutHeight = computed(() => {
    return isSmallerThanDesktop.value
      ? WALL_LAYOUT_THUMBNAIL_HEIGHTS.SMALLER_THAN_DESKTOP
      : WALL_LAYOUT_THUMBNAIL_HEIGHTS.DESKTOP
  })

  // Actions

  function closeOnboardingPanel(): void {
    void fetchWallOnboardingPanelHide()
    hasOnboardingCompleted.value = true
  }

  function dontShowGalleryAgain(templateId: string): void {
    void fetchDontShowGalleryAgain(templateId)
    hasOnboardingCompleted.value = true
  }

  async function fetchWallOnboardingPanelHide(): Promise<void> {
    try {
      await PadletApi.WallOnboardingPanel.fetch(window.app?.$store.getters?.wallId)
    } catch (e) {
      captureFetchException(e, { source: ' SurfaceWallOnboardingPanelHide' })
    }
  }

  async function fetchDontShowGalleryAgain(templateId: string): Promise<void> {
    try {
      await PadletApi.WallOnboardingPanel.dontShowGalleryAgain(window.app?.$store.getters?.wallId, templateId)
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceWallOnboardingPanelDontShowGalleryAgain' })
    }
  }

  function updateFormatSelected(format: string): void {
    formatSelected.value = format
  }

  return {
    // State
    formatSelected,

    // Getter
    showOnboardingPanel,
    galleryOnboardingFlowData,
    panelWidth,
    layoutWidth,
    layoutHeight,
    wallLayouts,
    isGalleryTemplatePanel,
    randomTitle,

    // Actions,
    closeOnboardingPanel,
    updateFormatSelected,
    dontShowGalleryAgain,
  }
})
